import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

interface RegistrationFormProps {
  attendeeType: "supporter" | "vip";
  onRegistrationComplete: () => void;
}

const RegistrationForm = ({ attendeeType, onRegistrationComplete }: RegistrationFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    real_estate_role: "",
    primary_market: "",
    tool_to_replace: "",
    primary_goal: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.full_name || !formData.email || !formData.phone || !formData.real_estate_role || !formData.primary_market) {
      toast.error("Please fill in all required fields.");
      return;
    }

    if (attendeeType === "vip" && !formData.tool_to_replace) {
      toast.error("Please tell us which tool you most want to replace or build.");
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke("summit-register", {
        body: {
          ...formData,
          attendee_type: attendeeType,
          primary_goal: formData.primary_goal || null,
        },
      });

      if (error) throw error;

      toast.success("Registration complete! Your access is now unlocked.");
      onRegistrationComplete();
    } catch (error: any) {
      console.error("Registration error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-10 border border-white/20">
      <h2 className="text-2xl font-bold text-white mb-2 text-center">
        {attendeeType === "vip" ? "Finish VIP Registration" : "Finish Registration"}
      </h2>
      <p className="text-white/60 text-sm mb-8 text-center">All fields marked with * are required</p>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Full Name */}
        <div>
          <Label htmlFor="full_name" className="text-white/90 mb-1.5 block">Full Name *</Label>
          <Input
            id="full_name"
            value={formData.full_name}
            onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
            className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
            placeholder="Your full name"
            required
            disabled={isSubmitting}
          />
        </div>

        {/* Email */}
        <div>
          <Label htmlFor="email" className="text-white/90 mb-1.5 block">Email Address *</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
            placeholder="your@email.com"
            required
            disabled={isSubmitting}
          />
        </div>

        {/* Phone */}
        <div>
          <Label htmlFor="phone" className="text-white/90 mb-1.5 block">Phone Number *</Label>
          <Input
            id="phone"
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
            placeholder="(555) 123-4567"
            required
            disabled={isSubmitting}
          />
        </div>

        {/* Role in Real Estate */}
        <div>
          <Label className="text-white/90 mb-1.5 block">Role in Real Estate *</Label>
          <Select
            value={formData.real_estate_role}
            onValueChange={(value) => setFormData({ ...formData, real_estate_role: value })}
            disabled={isSubmitting}
          >
            <SelectTrigger className="bg-white/10 border-white/20 text-white">
              <SelectValue placeholder="Select your role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="agent">Agent</SelectItem>
              <SelectItem value="broker">Broker</SelectItem>
              <SelectItem value="loan_officer">Loan Officer</SelectItem>
              <SelectItem value="investor">Investor</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Primary Market */}
        <div>
          <Label htmlFor="primary_market" className="text-white/90 mb-1.5 block">Primary Market or State *</Label>
          <Input
            id="primary_market"
            value={formData.primary_market}
            onChange={(e) => setFormData({ ...formData, primary_market: e.target.value })}
            className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
            placeholder="e.g., Atlanta, GA"
            required
            disabled={isSubmitting}
          />
        </div>

        {/* VIP Only: Tool to Replace */}
        {attendeeType === "vip" && (
          <div>
            <Label htmlFor="tool_to_replace" className="text-white/90 mb-1.5 block">
              Which tool do you most want to replace or build? *
            </Label>
            <Input
              id="tool_to_replace"
              value={formData.tool_to_replace}
              onChange={(e) => setFormData({ ...formData, tool_to_replace: e.target.value })}
              className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
              placeholder="e.g., Lead generation tool, CRM, etc."
              required
              disabled={isSubmitting}
            />
          </div>
        )}

        {/* Optional: Primary Goal */}
        <div>
          <Label className="text-white/90 mb-1.5 block">Primary Goal (optional)</Label>
          <Select
            value={formData.primary_goal}
            onValueChange={(value) => setFormData({ ...formData, primary_goal: value })}
            disabled={isSubmitting}
          >
            <SelectTrigger className="bg-white/10 border-white/20 text-white">
              <SelectValue placeholder="Select your primary goal" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="lead_generation">Lead generation</SelectItem>
              <SelectItem value="systems_automation">Systems and automation</SelectItem>
              <SelectItem value="compliance_ai_clarity">Compliance and AI clarity</SelectItem>
              <SelectItem value="product_building_ownership">Product building and ownership</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button
          type="submit"
          size="lg"
          className="w-full bg-gradient-to-r from-[#d8239b] to-[#ff69b4] hover:from-[#d8239b]/90 hover:to-[#ff69b4]/90 text-white font-bold py-6 text-lg"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Completing Registration...
            </>
          ) : (
            "Complete Registration"
          )}
        </Button>
      </form>
    </div>
  );
};

export default RegistrationForm;
